import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { ticketTypeActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const ticketTypeImportApi = createImportApi({
    actions: ticketTypeActions,
    capabilities,
    endpoint: 'workmgmt/tickettypes',
    templateFileName: 'TicketTypeImportTpl'
});
export const ApiTicketTypesImportCollection = ticketTypeImportApi.importCollection;
export const ApiTicketTypesDownloadImportModelItem = ticketTypeImportApi.downloadImportModel;

export const ApiTicketTypesGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickettypes/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.setList(response?.data?.list));
                    dispatch(ticketTypeActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('workmgmt/tickettypes/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiTicketTypesPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickettypes', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .post('workmgmt/tickettypes/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(ticketTypeActions.startExporting());
    }

    await gestopAPI
        .post(
            'workmgmt/tickettypes/export',
            { exportType, format, columns, filters, orderBy, groupBy },
            { ...config, responseType: 'blob' }
        )
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketTypeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(ticketTypeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .put(`workmgmt/tickettypes/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .patch(`workmgmt/tickettypes/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tickettypes/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .post(`workmgmt/tickettypes/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(ticketTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiTicketTypesGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(ticketTypeActions.startLoading());
    }

    await gestopAPI
        .get(`workmgmt/tickettypes/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(ticketTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(ticketTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
